import { useMemo, useRef, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  useFormContext,
  UseFormWatch,
} from "react-hook-form";
import { isNil, pick } from "lodash";

import { InputLabelHeight, OptionType } from "@covertree/types";

import Box from "@mui/material/Box";
import CardMedia from "@mui/material/CardMedia";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import {
  useMediaQuery,
  useTheme,
  PopoverProps,
  SelectProps,
} from "@mui/material";

import { ExpandMore } from "@mui/icons-material";
import { SxProps } from "@mui/system";

import {
  bindFocus,
  bindPopover,
  usePopupState,
} from "material-ui-popup-state/hooks";
import Popover from "material-ui-popup-state/HoverPopover";

import { Tooltip } from "../../content";
import { getInputLabelHeight } from "@covertree/utils";

const INPUT_HEIGT = 8;
const POPUP_Y_OFFSET = -(INPUT_HEIGT * 0.85);
const POPUP_X_OFFSET = -2;

type Props<
  TValues extends FieldValues,
  TOptionType extends string | number | null,
> = SelectProps & {
  id: string;
  name: string;
  control: Control<TValues>;
  watch?: UseFormWatch<TValues>;
  options: readonly OptionType<TOptionType>[];
  label: React.ReactNode;
  labelHeight?: InputLabelHeight;
  placeholder?: string;
  dataTestId?: string;
  definition?: Record<NonNullable<TOptionType>, React.ReactNode>;
  tooltip?: string;
  optional?: boolean;
  disabled?: boolean;
  inputStyle?: SxProps;
  capitalize?: boolean;
  fontSize?: number;
  labelFontSize?: number;
};

export const FormSelectField = <
  TValues extends FieldValues,
  TOptionType extends string | number | null,
>({
  id,
  name,
  control,
  options,
  label,
  labelHeight = InputLabelHeight.DEFAULT,
  placeholder,
  dataTestId = "dropdown-select",
  definition,
  tooltip,
  optional,
  disabled = false,
  inputStyle = {},
  capitalize = false,
  fontSize,
  labelFontSize,
  watch: watchDirectlyPassed,
}: Props<TValues, TOptionType>): JSX.Element => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const fontSizeValue = fontSize ? `${fontSize}px` : undefined;

  const watch = watchDirectlyPassed ?? useFormContext<TValues>().watch;

  const labelRef = useRef<HTMLParagraphElement>(null);

  const labelComponentHeight = useMemo(
    () => getInputLabelHeight(theme, labelHeight),
    [labelHeight, theme]
  );

  const popupState = usePopupState({
    variant: "popover",
    popupId: `${id}-popup`,
    disableAutoFocus: true,
  });
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const currentValue = watch(name as any) as keyof typeof definition;

  const [hoveredValue, setHoveredValue] =
    useState<keyof typeof definition>(currentValue);
  const [isSelectOpen, setSelectOpen] = useState<boolean>(false);

  const handleOpen = () => {
    setSelectOpen(true);
    setHoveredValue(currentValue);
  };

  const handleClose = () => {
    setSelectOpen(false);
  };

  const popoverProps = useMemo(() => {
    const bindingProps = bindPopover(popupState);

    return {
      ...bindingProps,
      open:
        bindingProps.open &&
        isSelectOpen &&
        !!definition &&
        !!definition[hoveredValue],
    } as PopoverProps;
  }, [definition, hoveredValue, isSelectOpen, popupState]);

  const optionalText = useMemo(() => {
    return optional ? (
      <Typography component="span" variant="subtitle1">
        Optional
      </Typography>
    ) : null;
  }, [optional]);

  const displayValue = useMemo(
    () => options.find(({ value }) => value === currentValue)?.label,
    [currentValue, options]
  );

  return (
    <div>
      <Controller
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        name={name as any}
        control={control}
        render={({ field, fieldState }) => {
          return (
            <FormControl
              variant="outlined"
              fullWidth
              error={fieldState.invalid}
              disabled={disabled}>
              {(label || tooltip) && (
                <InputLabel
                  htmlFor={id}
                  id={`${id}-label`}
                  variant="standard"
                  sx={{
                    height: isDesktop ? labelComponentHeight : "auto",
                    alignItems: "flex-end",
                    display: "flex",
                    position: "static",
                    transform: "none",
                    "& > p": {
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      width: "100%",
                      whiteSpace: "break-spaces",
                    },
                  }}
                  shrink>
                  <Typography
                    variant="body2"
                    ref={labelRef}
                    fontSize={labelFontSize ?? fontSize}>
                    {label}
                    {tooltip && (
                      <Tooltip
                        dataTestId={`${dataTestId}-tooltip`}
                        tooltip={tooltip ?? ""}
                        size="large"
                        fontSize={labelFontSize ?? fontSize}
                        sx={{ marginLeft: theme.spacing() }}
                        placement="bottom"
                      />
                    )}
                  </Typography>
                </InputLabel>
              )}
              <Select
                id={id}
                data-testid={dataTestId}
                open={isSelectOpen}
                onOpen={handleOpen}
                onClose={handleClose}
                displayEmpty
                IconComponent={ExpandMore}
                error={fieldState.invalid}
                labelId={`${id}-label`}
                disabled={disabled}
                inputProps={{
                  "data-testid": `${dataTestId}-select`,
                }}
                sx={{
                  backgroundColor: theme.palette.background.default,
                  "& > div": {
                    height: "100% !important",
                  },
                  ...(fontSize && {
                    fontSize: fontSizeValue,
                    height: fontSize * 3,
                    minHeight: fontSize * 3,
                  }),
                  ...inputStyle,
                }}
                renderValue={() => (
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      justifyContent: "space-between",
                      textTransform: capitalize ? "capitalize" : "none",
                      backgroundColor: disabled
                        ? theme.palette.action.disabledBackground
                        : theme.palette.background.default,
                      ...(inputStyle &&
                      "backgroundColor" in inputStyle &&
                      !disabled
                        ? (pick(inputStyle, ["backgroundColor"]) as SxProps)
                        : {}),
                    }}>
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}>
                      {!isNil(currentValue) ? (
                        displayValue
                      ) : (
                        <Typography
                          component="span"
                          color="textSecondary"
                          fontSize={fontSize}>
                          {placeholder}
                        </Typography>
                      )}
                    </Box>
                    {optionalText}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 450,
                    },
                  },
                }}
                {...bindFocus(popupState)}
                {...field}>
                {options.length === 0 ? (
                  <MenuItem
                    sx={{ ...(fontSize && { fontSize: fontSizeValue }) }}>
                    {placeholder}
                  </MenuItem>
                ) : (
                  options.map((option) => (
                    <MenuItem
                      sx={{
                        [theme.breakpoints.down("xs")]: {
                          whiteSpace: "pre-wrap",
                        },
                        textWrap: "wrap",
                        textTransform: capitalize ? "capitalize" : "none",
                        ...(fontSize && { fontSize: fontSizeValue }),
                      }}
                      key={String(option.value)}
                      value={option.value as NonNullable<TOptionType>}
                      onMouseEnter={() =>
                        setHoveredValue(
                          String(option.value) as keyof typeof definition
                        )
                      }>
                      {option.icon && (
                        <CardMedia
                          sx={{
                            width: theme.spacing(5),
                            height: theme.spacing(5),
                            marginRight: theme.spacing(2.5),
                          }}
                          data-testid={`${option.value}-image`}
                          image={option.icon}
                        />
                      )}
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </Select>
              <Box
                position="absolute"
                // Height is set to fix ui for 2 lines messages
                height={28}
                bottom={theme.spacing(-3)}>
                <FormHelperText
                  error={fieldState.invalid}
                  {...(!fieldState.error?.message && { sx: { margin: 0 } })}>
                  {fieldState.error?.message}
                </FormHelperText>
              </Box>
            </FormControl>
          );
        }}
      />

      {!!definition && isDesktop && !!definition[hoveredValue] && (
        <Popover
          {...popoverProps}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          disableScrollLock
          transformOrigin={{
            vertical: POPUP_Y_OFFSET * 8,
            horizontal: POPUP_X_OFFSET * 8,
          }}>
          {definition[hoveredValue]}
        </Popover>
      )}
    </div>
  );
};
